import type { Effect, Store } from 'effector'
import type { History, Location, QueryValues, To } from './index.h'
import type { Route } from './router'

import {
  attach,
  createEffect,
  createEvent,
  createStore,
  restore,
  sample,
} from 'effector'
import { debounce, previous } from 'patronum'
import { register } from '~/core/repository'
import { Action } from './index.h'
import { UPDATE_QUERY_DEBOUNCE_TIMEOUT, updateQuery } from './query'
import { init as addRoutes } from './router'

//
// base history events and stores
//

export const init = createEvent<{ history: History; routes: Route }>()
const locationUpdated = createEvent<{ location: Location; action: Action }>()

export const $history = createStore<N<History>>(null) //
  .on(init, (__, { history }) => history)
export const $location = createStore<N<Location>>(null)
  .on(init, (__, { history }) => history.location)
  .on(locationUpdated, (__, { location }) => location)
export const $action = createStore<N<Action>>(null)
  .on(init, () => Action.Pop)
  .on(locationUpdated, (__, { action }) => action)

// stores for parts of location
export const $pathname: Store<N<string>> = $location.map(prop('pathname'))
export const $search: Store<N<string>> = $location.map(prop('search'))
export const $hash: Store<N<string>> = $location.map(prop('hash'))
export const $key: Store<N<string>> = $location.map(prop('key'))
export const $state: Store<N<unknown>> = $location.map(prop('state'))
export const $href: Store<N<string>> = $location.map((location) =>
  location ? location.pathname + location.search : null
)

// parsed query string store
export const $query: Store<URLSearchParams> = $search.map(
  (search) => new URLSearchParams(search || undefined)
)

//
// get previous location
//

export const $previous: Store<N<Location>> = previous($location)

//
// history subscription management
//

type Unsub = () => void
type N<T> = T | null

const subscribeToLocationFx: Effect<
  { unlisten: N<Unsub>; history: History },
  Unsub
> = createEffect<{ unlisten: N<Unsub>; history: History }, Unsub>(
  ({ unlisten, history }) => {
    if (unlisten) unlisten()
    return history.listen(locationUpdated)
  }
)

const $locationSubscription = restore(subscribeToLocationFx, null)

sample({
  clock: init,
  source: $locationSubscription,
  fn: (unlisten, { history }) => ({ unlisten, history }),
  target: subscribeToLocationFx,
})

//
// handle navigations
//

export type ToEx = To & { state?: unknown }

export const navigatePush = createEvent<ToEx>()
export const navigateReplace = createEvent<ToEx>()
export const navigateGo = createEvent<number>()
export const navigateBack = createEvent()
export const navigateBackOrHome = createEvent()
export const navigateForward = createEvent()

const navigatePushFx: Effect<ToEx, void> = attach({
  source: $history,
  effect: (history, to: ToEx) => history?.push(to, to.state),
})

const navigateReplaceFx: Effect<ToEx, void> = attach({
  source: $history,
  effect: (history, to: ToEx) => history?.replace(to, to.state),
})

const navigateGoFx: Effect<number, void> = attach({
  source: $history,
  effect: (history, delta: number) => history?.go(delta),
})

const navigateBackFx: Effect<void, void> = attach({
  source: $history,
  effect: (history) => history?.back(),
})

const navigateForwardFx: Effect<void, void> = attach({
  source: $history,
  effect: (history) => history?.forward(),
})

sample({ clock: navigatePush, target: navigatePushFx })
sample({ clock: navigateReplace, target: navigateReplaceFx })
sample({ clock: navigateGo, target: navigateGoFx })
sample({ clock: navigateBack, target: navigateBackFx })
sample({ clock: navigateForward, target: navigateForwardFx })
sample({
  clock: navigateBackOrHome,
  filter: () =>
    typeof window !== 'undefined' && (window.history.state?.idx ?? 0) > 0,
  target: navigateBackFx,
})
sample({
  clock: navigateBackOrHome,
  filter: () =>
    typeof window === 'undefined' || (window.history.state?.idx ?? 0) <= 0,
  fn: () => '/',
  target: navigateReplaceFx,
})

//
// handle partial query string updates navigations
//

export const navigatePushQuery = createEvent<QueryValues>()
export const navigateReplaceQuery = createEvent<QueryValues>()
export const navigatePushUpdatedQuery = createEvent()
export const navigateReplaceUpdatedQuery = createEvent()

const navigatePushUpdatedQueryFx: Effect<void, void> = attach({
  source: { history: $history, query: $query },
  effect: ({ history, query }) => {
    history?.push({ search: query.toString() }, { skipScrollToTop: true })
  },
})

const navigateReplaceUpdatedQueryFx: Effect<void, void> = attach({
  source: { history: $history, query: $query },
  effect: ({ history, query }) => {
    history?.replace({ search: query.toString() })
  },
})

const navigatePushQueryFx: Effect<QueryValues, void> = attach({
  source: $query,
  effect: (query, to: QueryValues) => {
    if (updateQuery(query, to)) {
      navigatePushUpdatedQuery()
    }
  },
})

const navigateReplaceQueryFx: Effect<QueryValues, void> = attach({
  source: $query,
  effect: (query, to: QueryValues) => {
    if (updateQuery(query, to)) {
      navigateReplaceUpdatedQuery()
    }
  },
})

sample({ clock: navigatePushQuery, target: navigatePushQueryFx })
sample({ clock: navigateReplaceQuery, target: navigateReplaceQueryFx })

const navigatePushUpdatedQueryDebounced = debounce({
  source: navigatePushUpdatedQuery,
  timeout: UPDATE_QUERY_DEBOUNCE_TIMEOUT,
})

const navigateReplaceUpdatedQueryDebounced = debounce({
  source: navigateReplaceUpdatedQuery,
  timeout: UPDATE_QUERY_DEBOUNCE_TIMEOUT,
})

sample({
  clock: navigatePushUpdatedQueryDebounced,
  target: navigatePushUpdatedQueryFx,
})
sample({
  clock: navigateReplaceUpdatedQueryDebounced,
  target: navigateReplaceUpdatedQueryFx,
})

//
// Refresh current route
// This is dumb event, which does nothing here,
// but it is handled by navigation process, causeing retriggering route's `wait` and `init` events
//

export const refresh = createEvent()

//
// transitions
//

export const transitionStart = createEvent()
export const transitionEnd = createEvent()

const $transactionsCount = createStore<number>(0)
  .on(transitionStart, (count) => count + 1)
  .on(transitionEnd, (count) => count - 1)

export const $ongoingTransition: Store<boolean> = $transactionsCount.map(
  (count) => count > 0
)

//
// helper functions
//

function prop<K extends keyof Location>(
  property: K
): (location: N<Location>) => N<Location[K]> {
  return (location) =>
    location ? (location[property] as string) || null : null
}

//
// Add application routes to the router
//

// add top-level routes to the router
// this effect is executed synchronously, so it should run before any location listeners
const addRoutesFx: Effect<Route, void> = createEffect(addRoutes)

sample({
  clock: init,
  fn: ({ routes }) => routes,
  target: addRoutesFx,
})

//
// register stores and events
//

register($pathname, '$route/pathname')
register($search, '$route/search')
register($hash, '$route/hash')
register($key, '$route/key')
register($state, '$route/state')
register($query, '$route/query')
register($href, '$route/href')
register(navigateBack, 'route/navigateBack')
register(navigateBackOrHome, 'route/navigateBackOrHome')
